import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChatCircle } from '@phosphor-icons/react'
import api from '../services/api'
import { useAuth } from '../context/AuthContext'
import { useWebSocketOptional } from '../context/WebSocketContext'

interface ChatNotification {
  id: string
  type: string
  title: string
  message: string
  reference_id: string
  read: boolean
  created_at: string
}

const CHAT_TYPE = 'new_chat_message'

/**
 * Dedicated chat inbox next to the notification bell (Plane #7). Surfaces only
 * new-chat-message notifications so a shipper/agent sees pending replies at a
 * glance, separate from the general bell. Clicking a row opens that order's
 * chat tab.
 */
export function ChatBell() {
  const [items, setItems] = useState<ChatNotification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { user } = useAuth()
  const ws = useWebSocketOptional()

  const fetchItems = async () => {
    try {
      const res = await api.get<{ notifications: ChatNotification[]; unreadCount: number }>(
        `/notifications?limit=10&type=${CHAT_TYPE}`
      )
      setItems(res.data.notifications)
      setUnreadCount(res.data.unreadCount)
    } catch (error) {
      console.error('Error fetching chat notifications:', error)
    }
  }

  const fetchUnreadCount = async () => {
    try {
      const res = await api.get<{ unreadCount: number }>(`/notifications/unread-count?type=${CHAT_TYPE}`)
      setUnreadCount(res.data.unreadCount)
    } catch (error) {
      console.error('Error fetching chat unread count:', error)
    }
  }

  useEffect(() => {
    if (!user) return
    fetchItems()
    const interval = setInterval(fetchUnreadCount, 30000)
    return () => clearInterval(interval)
  }, [user])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (isOpen) fetchItems()
  }, [isOpen])

  // Realtime: refresh the instant a chat notification arrives.
  useEffect(() => {
    if (!user || !ws) return
    const unsubscribe = ws.onNotification(() => fetchItems())
    return unsubscribe
  }, [user, ws])

  const markAsRead = async (id: string) => {
    try {
      await api.post(`/notifications/${id}/read`)
      setItems(prev => prev.map(n => (n.id === id ? { ...n, read: true } : n)))
      setUnreadCount(prev => Math.max(0, prev - 1))
    } catch (error) {
      console.error('Error marking chat notification as read:', error)
    }
  }

  const handleClick = (n: ChatNotification) => {
    if (!n.read) markAsRead(n.id)
    const companyType = user?.current_company?.type
    const [orderId, messageId] = n.reference_id.split(':')
    const basePath = companyType === 'shipper' ? '/shipper' : '/freight-forwarder'
    navigate(messageId
      ? `${basePath}/orders/${orderId}?tab=chat&messageId=${messageId}`
      : `${basePath}/orders/${orderId}?tab=chat`)
    setIsOpen(false)
  }

  const formatTime = (dateString: string) => {
    const date = new Date(dateString)
    const diff = Date.now() - date.getTime()
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)
    if (minutes < 1) return 'Agora'
    if (minutes < 60) return `${minutes}min`
    if (hours < 24) return `${hours}h`
    if (days < 7) return `${days}d`
    return date.toLocaleDateString('pt-BR')
  }

  if (!user) return null

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
        title="Mensagens"
      >
        <ChatCircle size={24} weight={unreadCount > 0 ? 'fill' : 'regular'} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[20px] h-5 flex items-center justify-center px-1 text-xs font-bold text-white bg-red-500 rounded-full animate-pulse">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden z-50">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Mensagens</h3>
            {unreadCount > 0 && (
              <span className="text-xs text-gray-500">{unreadCount} não lida{unreadCount > 1 ? 's' : ''}</span>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="py-12 px-4 text-center">
                <ChatCircle size={48} className="mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500 text-sm">Nenhuma mensagem</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {items.map((n) => (
                  <button
                    key={n.id}
                    onClick={() => handleClick(n)}
                    className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex gap-3 ${
                      !n.read ? 'bg-purple-50/50' : ''
                    }`}
                  >
                    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-purple-100 text-purple-600">
                      <ChatCircle size={20} weight="fill" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm font-medium ${!n.read ? 'text-gray-900' : 'text-gray-700'}`}>
                          {n.title}
                        </p>
                        <span className="text-xs text-gray-400 flex-shrink-0">{formatTime(n.created_at)}</span>
                      </div>
                      <p className="text-sm text-gray-500 truncate mt-0.5">{n.message}</p>
                    </div>
                    {!n.read && <div className="w-2 h-2 rounded-full bg-purple-500 flex-shrink-0 mt-2" />}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
